import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  IDENTITY,
  clampPan,
  zoomAt,
  type Viewport,
} from '../utils/presentationZoom';
import PresentationModeIndicator from '../components/PresentationModeIndicator';

/**
 * Presentation Mode — magnify-and-pan for showing 360 on a TV.
 *
 * Browser zoom is useless here: it changes the effective viewport width, so
 * media queries, ResizeObserver, TanTable column sizing and virtualized row math
 * all re-fire and the layout REFLOWS instead of magnifying. V1 gets away with it
 * because it's a static page.
 *
 * So we freeze <body>'s layout box to the viewport size captured on entry and
 * put a transform on it. Nothing re-measures — the magnification is pure paint.
 *
 * Scaling <body> specifically (not an inner container) buys three things free:
 *   1. Portals align. Modals, tooltips, TanTable menus and DateTimePicker
 *      popovers all mount into <body>, so they ride inside the transform.
 *   2. `position: fixed` aligns. A transformed ancestor becomes the containing
 *      block for fixed descendants, so overlays resolve against the frozen box.
 *   3. Clicks map natively — the browser inverts the transform when hit testing.
 * That's what makes the mode fully interactive with no changes to modal,
 * tooltip, TanTable or DateTimePicker code.
 */

type PresentationModeValue = {
  active: boolean;
  scale: number;
  toggle: () => void;
  exit: () => void;
};

const PresentationModeContext = createContext<PresentationModeValue | undefined>(undefined);

/** Wheel deltas are device-dependent; exponential keeps the feel even across them. */
const WHEEL_ZOOM_RATE = 0.002;
const KEY_ZOOM_STEP = 1.25;

const CSS_VARS = ['--pm-vw', '--pm-vh', '--pm-x', '--pm-y', '--pm-scale'] as const;

export const PresentationModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [active, setActive] = useState(false);
  const [view, setView] = useState<Viewport>(IDENTITY);

  // Listeners are installed once and read through refs, so we never rebind on
  // every zoom step.
  const activeRef = useRef(active);
  activeRef.current = active;
  const viewRef = useRef(view);
  viewRef.current = view;

  const applyView = useCallback((next: Viewport) => {
    setView(clampPan(next, window.innerWidth, window.innerHeight));
  }, []);

  const exit = useCallback(() => {
    setActive(false);
    setView(IDENTITY);
  }, []);

  const toggle = useCallback(() => {
    setActive((wasActive) => {
      if (wasActive) setView(IDENTITY);
      return !wasActive;
    });
  }, []);

  // Paint the transform. useLayoutEffect so the body is correct in the same
  // frame the state flips — no flash of unscaled content on entry.
  useLayoutEffect(() => {
    const { body } = document;
    if (!active) {
      body.removeAttribute('data-presentation');
      CSS_VARS.forEach((name) => body.style.removeProperty(name));
      return;
    }
    body.setAttribute('data-presentation', '');
    body.style.setProperty('--pm-vw', `${window.innerWidth}px`);
    body.style.setProperty('--pm-vh', `${window.innerHeight}px`);
    body.style.setProperty('--pm-x', `${view.x}px`);
    body.style.setProperty('--pm-y', `${view.y}px`);
    body.style.setProperty('--pm-scale', String(view.scale));
  }, [active, view]);

  // Re-snapshot the frozen box when the window changes size. Without this the
  // box and the real viewport diverge and every `vh`/`vw` inside the app — modal
  // max-heights especially — resolves against the wrong one.
  useEffect(() => {
    if (!active) return undefined;
    const onResize = () => {
      document.body.style.setProperty('--pm-vw', `${window.innerWidth}px`);
      document.body.style.setProperty('--pm-vh', `${window.innerHeight}px`);
      applyView(viewRef.current);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [active, applyView]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;

      // Toggle. Deliberately NOT routed through useAltHotkeys: that hook keeps a
      // layer stack, so an open modal would swallow the chord. It also rejects
      // Ctrl/Cmd, which is why this combination can never collide with the
      // Alt+letter chords carrying V1 muscle memory.
      if (mod && e.altKey && e.code === 'KeyP') {
        e.preventDefault();
        toggle();
        return;
      }

      if (!activeRef.current) return;

      if (e.key === 'Escape') {
        // Capture phase + stopPropagation: zoomed-and-in-a-modal, the first Esc
        // backs out of the zoom, the second closes the modal.
        e.preventDefault();
        e.stopPropagation();
        exit();
        return;
      }

      if (!mod || e.altKey) return;
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      if (e.code === 'Equal' || e.code === 'NumpadAdd') {
        e.preventDefault();
        applyView(zoomAt(viewRef.current, KEY_ZOOM_STEP, cx, cy));
      } else if (e.code === 'Minus' || e.code === 'NumpadSubtract') {
        e.preventDefault();
        applyView(zoomAt(viewRef.current, 1 / KEY_ZOOM_STEP, cx, cy));
      } else if (e.code === 'Digit0' || e.code === 'Numpad0') {
        e.preventDefault();
        setView(IDENTITY);
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [toggle, exit, applyView]);

  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      // Only while active, so ordinary browser zoom is untouched the rest of the
      // time. Plain scroll always falls through to the grids.
      if (!activeRef.current || !(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const factor = Math.exp(-e.deltaY * WHEEL_ZOOM_RATE);
      applyView(zoomAt(viewRef.current, factor, e.clientX, e.clientY));
    };
    // passive: false — a passive listener cannot preventDefault, and without
    // that the browser's own zoom fires alongside ours.
    window.addEventListener('wheel', onWheel, { passive: false, capture: true });
    return () => window.removeEventListener('wheel', onWheel, { capture: true } as EventListenerOptions);
  }, [applyView]);

  // Ctrl+drag to pan. Ctrl is required because a bare drag has to stay with the
  // app: text selection, TanTable column resize, dispatch board drag-and-drop.
  useEffect(() => {
    let from: { x: number; y: number; view: Viewport } | null = null;

    const onDown = (e: MouseEvent) => {
      if (!activeRef.current || !(e.ctrlKey || e.metaKey) || e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      from = { x: e.clientX, y: e.clientY, view: viewRef.current };
    };
    const onMove = (e: MouseEvent) => {
      if (!from) return;
      e.preventDefault();
      applyView({
        scale: from.view.scale,
        x: from.view.x + (e.clientX - from.x),
        y: from.view.y + (e.clientY - from.y),
      });
    };
    const onUp = () => {
      from = null;
    };

    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('mousemove', onMove, true);
    window.addEventListener('mouseup', onUp, true);
    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('mousemove', onMove, true);
      window.removeEventListener('mouseup', onUp, true);
    };
  }, [applyView]);

  const value = useMemo<PresentationModeValue>(
    () => ({ active, scale: view.scale, toggle, exit }),
    [active, view.scale, toggle, exit],
  );

  return (
    <PresentationModeContext.Provider value={value}>
      {children}
      <PresentationModeIndicator active={active} scale={view.scale} />
    </PresentationModeContext.Provider>
  );
};

export function usePresentationMode(): PresentationModeValue {
  const ctx = useContext(PresentationModeContext);
  if (!ctx) {
    throw new Error('usePresentationMode must be used within a PresentationModeProvider');
  }
  return ctx;
}

export default PresentationModeContext;
